/**
 * Header for the Discussion view.  Holds the count of items and a filter bar
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";
import {useCallback} from "react";
import {connect} from "react-redux";

import {View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Spring} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import {mediaQueries} from "@repo/shared-styles";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import {getKaid} from "#data-access/profile-utils.ts";
import useProfile from "#data-access/use-profile.ts";
import LocalStore from "#shared-package/local-store.ts";

import {discussionContainer} from "../shared-styles.ts";
import type {SortByType} from "../types.ts";

import {changeSortByAction} from "./discussion-actions.ts";
import type {State as DiscussionState} from "./discussion-reducer.ts";

type OwnProps = Empty;

type ReduxProps = {
    currentSort: SortByType;
    onSortByChanged: (sortBy: SortByType, gqlClient: ApolloGqlClient) => void;
};

type Props = OwnProps & ReduxProps & WithApolloProps;

function DiscussionHeader(props: Props): React.ReactElement {
    const {currentSort, onSortByChanged, gqlClient} = props;
    const profileResult = useProfile();
    const profile =
        profileResult.status === "success" ? profileResult.profile : null;
    const kaid = getKaid(profile);

    const handleFilterChanged = useCallback(
        (selected: string) => {
            if (kaid) {
                LocalStore.set("sort_type:" + kaid, selected);
            }
            onSortByChanged(selected as any, gqlClient);
        },
        [onSortByChanged, gqlClient, kaid],
    );

    return (
        <View style={styles.container}>
            <Spring />
            <View style={styles.filterContainer}>
                <LabelLarge tag="label" htmlFor="sortBy">
                    {t`Sort by:`}
                </LabelLarge>
                <SingleSelect
                    onChange={handleFilterChanged}
                    selectedValue={currentSort}
                    style={styles.select}
                    id="sortBy"
                    placeholder=""
                >
                    {[
                        {
                            value: "trending",
                            label: t`Trending`,
                        },
                        {
                            value: "top-voted",
                            label: t`Top Voted`,
                        },
                        {
                            value: "recent",
                            label: t`Recent`,
                        },
                    ].map((opt) => (
                        <OptionItem
                            value={opt.value}
                            key={opt.value}
                            label={opt.label}
                        />
                    ))}
                </SingleSelect>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        ...discussionContainer,
        flexDirection: "row",
        justifyContent: "space-between",
        paddingTop: spacing.large_24,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,

        [mediaQueries.xsOrSmaller]: {
            paddingLeft: 8,
            flexDirection: "column",
        },
    },

    filterContainer: {
        flexDirection: "row",
        alignItems: "center",
    },

    select: {
        minWidth: 176,
        marginLeft: 8,
        fontSize: 14,
    },
});

const mapStateToProps = (state: DiscussionState) => {
    return {currentSort: state.currentSort};
};

const mapDispatchToProps = {
    onSortByChanged: changeSortByAction,
} as const;

type ExportProps = Without<
    WithoutApollo<
        JSX.LibraryManagedAttributes<
            typeof DiscussionHeader,
            React.ComponentProps<typeof DiscussionHeader>
        >
    >,
    ReduxProps
>;

export default compose(
    withApollo,
    connect(mapStateToProps, mapDispatchToProps),
)(DiscussionHeader) as React.ComponentType<ExportProps>;
